import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { http, HttpResponse, delay } from 'msw';

import { api } from 'lib/api-client';
import { MutationConfig } from 'lib/react-query';
import { TranscriptionResponse } from 'types/api';
import { env } from 'config/env';

export const transcriptionInputSchema = z.object({
    audio: z.instanceof(Blob, { message: 'Audio must be a valid Blob' }),
    post_processing: z.string().optional().nullable(),
});

export type TranscriptionInput = z.infer<typeof transcriptionInputSchema>;

export const transcribeAudio = async (
    data: TranscriptionInput,
): Promise<TranscriptionResponse> => {
    const formData = new FormData();
    formData.append('audio', data.audio, 'recording.webm');
    if (data.post_processing) {
        formData.append('post_processing', data.post_processing);
    }

    return api.post<TranscriptionResponse>(
        '/app/api/v1/transcription',
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    ) as unknown as Promise<TranscriptionResponse>;
};

type UseTranscribeAudioOptions = {
    mutationConfig?: MutationConfig<typeof transcribeAudio>;
};

export const useTranscribeAudio = ({
    mutationConfig,
}: UseTranscribeAudioOptions = {}) => {
    return useMutation({
        ...mutationConfig,
        mutationFn: transcribeAudio,
    });
};

export const transcribeAudioMSWHandler = (
    response?: Partial<TranscriptionResponse>,
) => {
    const defaultResponse: TranscriptionResponse = {
        transcription:
            'This is a mock transcription of your audio recording. In a real application, this would be the actual transcribed text from your voice input.',
        pre_processed_transcription: null,
        ...response,
    };

    return http.post(`${env.API_URL}/app/api/v1/transcription`, async () => {
        await delay(2000);
        return HttpResponse.json(defaultResponse);
    });
};
